import { NewOrientations, Orientations } from '@/enums/products'
import { GET_SINGLE_DESIGN, GET_MEMBERSHIP_TYPES, GET_FLAGS, SIGNUP_GET_LATEST_DESIGN } from '@/utils/store/actionTypes'
import { SET_FORM_FIELD } from '@/utils/store/mutationTypes'
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'
import { IS_FLAGS_FETCHED } from '@/utils/store/getterTypes'
import { MembershipTypesToNewOrientations } from '@/enums/member'
import { capitalize } from '@/utils/general'
import { getMembershipTypeDesignWithMostStock, groupMembershipTypesSizesForDesign } from '@/utils/products'
import { OnePagerTypes } from '@/enums/signup'

export default ({ fetchLatestDesign = false, type = null } = {}) => {
  return {
    async fetch () {
      const shouldFetchLatestDesign = fetchLatestDesign || !this.designSlug

      const designCall = shouldFetchLatestDesign ? this.getAndSetLatestDesign : this.getAndSetDesign

      await Promise.all([
        designCall(),
        this.getAndSetMembershipTypes(),
        this.getAndSetFlags()
      ])

      if (this.isOnePagerWithDesignChoice) {
        this.checkForFeaturedDesigns()
      }

      this.checkIfCurrentSizeAvailable()
    },
    data () {
      return {
        designLoading: false,
        designResponse: null,
        secondaryDesignResponse: null,
        membershipTypesLoading: false,
        membershipTypes: [],
        triggerSignupRedirect: false
      }
    },
    watch: {
      triggerSignupRedirect: {
        immediate: true,
        handler (newVal) {
          if (newVal) {
            this.$nextTick(() => {
              if (this.isWomenOnePager) {
                this.$router.push('/women/signup/choose-design')
              } else {
                this.$router.push('/men/signup/choose-design')
              }
            })
          }
        }
      }
    },
    computed: {
      isOnePagerWithDesignChoice () {
        return type === OnePagerTypes.withDesignChoice
      },
      isWomenOnePager () {
        return this.orientation === NewOrientations.women
      },
      productGroup () {
        return (this.form.membershipType || '').replace('-membership', '')
      },
      orientation () {
        const orientation = MembershipTypesToNewOrientations[`${this.productGroup}-membership`]
        return orientation || NewOrientations.men
      },
      fixedDesignSlug () {
        return this.form.fixedDesign.slug
      },
      secondaryFixedDesignSlug () {
        return this.form.secondaryFixedDesign.slug
      },
      designSlug () {
        return this.isWomenOnePager ? this.form[Orientations.womenbralette].design.slug : this.form.design.slug
      },
      secondaryDesignSlug () {
        return this.form[Orientations.womenhipster].design.slug
      },
      sizeReference () {
        return this.isWomenOnePager ? this.form[Orientations.womenbralette].size.reference : this.form.size.reference
      },
      secondarySizeReference () {
        return this.form[Orientations.womenhipster].size.reference
      },
      onePageProps () {
        return {
          designResponse: this.designResponse,
          secondaryDesignResponse: this.secondaryDesignResponse,
          membershipTypes: this.membershipTypes,
          orientation: this.orientation,
          productGroup: this.productGroup,
          fixedDesignSlug: this.fixedDesignSlug,
          secondaryFixedDesignSlug: this.secondaryFixedDesignSlug,
          designSlug: this.designSlug,
          secondaryDesignSlug: this.secondaryDesignSlug,
          sizeReference: this.sizeReference,
          secondarySizeReference: this.secondarySizeReference
        }
      },
      ...mapGetters('flags', {
        isFlagsFetched: IS_FLAGS_FETCHED
      }),
      ...mapState('signup', {
        form: state => state.form
      })
    },
    methods: {
      async getAndSetFlags () {
        if (this.isFlagsFetched) {
          return
        }
        try {
          await this.getFlags()
        } catch (error) {
          this.$ota.general.logError(error)
        }
      },
      async getAndSetDesign () {
        this.designLoading = true

        try {
          if (this.isWomenOnePager) {
            const [braletteDesign, hipsterDesign] = await Promise.all([
              this.getSingleDesign({ slug: this.designSlug, product_group: this.productGroup }),
              this.getSingleDesign({ slug: this.secondaryDesignSlug, product_group: this.productGroup })
            ])

            this.designResponse = braletteDesign
            this.secondaryDesignResponse = hipsterDesign

            this.setSignupFormField({ key: 'fixedDesign', value: { name: braletteDesign.name, slug: braletteDesign.slug } })
            this.setSignupFormField({ key: 'secondaryFixedDesign', value: { name: hipsterDesign.name, slug: hipsterDesign.slug } })

            if (!braletteDesign.visibleInFixedSignup || !hipsterDesign.visibleInFixedSignup) {
              this.triggerSignupRedirect = true
              return
            }
          } else {
            const design = await this.getSingleDesign({ slug: this.designSlug, product_group: this.productGroup })
            this.setSignupFormField({ key: 'fixedDesign', value: { name: design.name, slug: design.slug } })
            this.designResponse = design

            if (!design.visibleInFixedSignup) {
              this.triggerSignupRedirect = true
              return
            }
          }
        } catch (error) {
          if (error.response.status === 404) {
            await this.getAndSetLatestDesign()
          }

          this.$ota.general.logError(error)
        }
        this.designLoading = false
      },
      async getAndSetLatestDesign () {
        try {
          const variables = {
            women: this.orientation === NewOrientations.women,
            men: this.orientation === NewOrientations.men || this.orientation === NewOrientations.boys,
            addon: this.orientation === NewOrientations.addon,
            boys: this.orientation === NewOrientations.boys || this.orientation === NewOrientations.men
          }

          const designs = await this.getLatestDesign(variables)

          if (!this.isWomenOnePager) {
            const currentProductGroupDesign = designs.find(design => design.productGroups.find(productGroup => productGroup.reference === this.productGroup))

            if (currentProductGroupDesign) {
              this.setSignupFormField({ key: 'design', value: { name: currentProductGroupDesign.name, slug: currentProductGroupDesign.slug } })
              this.setSignupFormField({ key: 'freeDesign', value: { name: currentProductGroupDesign.name, slug: currentProductGroupDesign.slug } })
              this.setSignupFormField({ key: 'fixedDesign', value: { name: currentProductGroupDesign.name, slug: currentProductGroupDesign.slug } })
              this.designResponse = currentProductGroupDesign
            }
          } else {
            const braletteDesign = designs.find(design => design.productGroups.find(productGroup => productGroup.reference === Orientations.womenbralette))
            const hipsterDesign = designs.find(design => design.productGroups.find(productGroup => productGroup.reference === Orientations.womenhipster))

            if (braletteDesign) {
              this.designResponse = braletteDesign

              this.setSignupFormField({
                key: Orientations.womenbralette,
                value: {
                  ...this.form[Orientations.womenbralette],
                  ...{ design: { name: capitalize(braletteDesign.slug.replace('-', ' ')), slug: braletteDesign.slug } }
                }
              })
              this.setSignupFormField({ key: 'fixedDesign', value: { name: braletteDesign.name, slug: braletteDesign.slug } })
            }

            if (hipsterDesign) {
              this.secondaryDesignResponse = hipsterDesign

              this.setSignupFormField({
                key: Orientations.womenhipster,
                value: {
                  ...this.form[Orientations.womenhipster],
                  ...{ design: { name: capitalize(hipsterDesign.slug.replace('-', ' ')), slug: hipsterDesign.slug } }
                }
              })
              this.setSignupFormField({ key: 'secondaryFixedDesign', value: { name: hipsterDesign.name, slug: hipsterDesign.slug } })
            }
          }
        } catch (error) {
          this.$ota.general.logError(error)
        }
      },
      async getAndSetMembershipTypes () {
        this.membershipTypesLoading = true
        const variables = {
          women: this.orientation === NewOrientations.women,
          men: this.orientation === NewOrientations.men || this.orientation === NewOrientations.boys,
          addon: this.orientation === NewOrientations.addon,
          boys: this.orientation === NewOrientations.boys || this.orientation === NewOrientations.men
        }
        try {
          const membershipTypes = await this.getMembershipTypes(variables)
          if (membershipTypes.length) {
            this.membershipTypes = membershipTypes
          }
        } catch (e) {
          this.$ota.general.logError(e)
          this.$otoaster.danger(this.$t('Error.general'))
        }
        this.membershipTypesLoading = false
      },
      checkIfCurrentSizeAvailable () {
        if (!this.designSlug || (this.isWomenOnePager && !this.secondaryDesignSlug)) {
          return
        }

        const productGroup = this.isWomenOnePager ? Orientations.womenbralette : this.form.membershipType.replace('-membership', '')
        const sizeOptions = groupMembershipTypesSizesForDesign(this.membershipTypes, this.designSlug)[productGroup]
        const currentSize = this.isWomenOnePager ? this.form[Orientations.womenbralette].size.reference : this.form.size.reference

        if (currentSize) {
          const isCurrentSizeInStockAndValid = sizeOptions.find(size => size.value === currentSize && size.inStock)

          if (isCurrentSizeInStockAndValid) {
            return
          } else {
            this.resetSizes({ type: 'primary' })
          }
        }

        const isThereAnyStockAvailable = sizeOptions.find(size => size.inStock)

        if (!isThereAnyStockAvailable) {
          const designWithMostStock = getMembershipTypeDesignWithMostStock(this.membershipTypes, productGroup)

          this.setSignupFormField({ key: 'design', value: { name: designWithMostStock.name, slug: designWithMostStock.reference } })
          this.setSignupFormField({ key: 'freeDesign', value: { name: designWithMostStock.name, slug: designWithMostStock.reference } })
          this.setSignupFormField({ key: 'fixedDesign', value: { name: designWithMostStock.name, slug: designWithMostStock.reference } })
        }

        if (this.isWomenOnePager) {
          // Set hipster size
          const productGroup = Orientations.womenhipster
          const sizeOptions = groupMembershipTypesSizesForDesign(this.membershipTypes, this.secondaryDesignSlug)[productGroup]
          const currentSize = this.form[Orientations.womenhipster].size.reference

          if (currentSize) {
            const isCurrentSizeInStockAndValid = sizeOptions.find(size => size.value === currentSize && size.inStock)

            if (isCurrentSizeInStockAndValid) {
              return
            } else {
              this.resetSizes({ type: 'secondary' })
            }
          }

          const isThereAnyStockAvailable = sizeOptions.find(size => size.inStock)

          if (!isThereAnyStockAvailable) {
            const designWithMostStock = getMembershipTypeDesignWithMostStock(this.membershipTypes, productGroup)

            this.setSignupFormField({
              key: Orientations.womenbralette,
              value: {
                ...this.form[Orientations.womenbralette],
                ...{ design: { name: designWithMostStock.name, slug: designWithMostStock.reference } }
              }
            })

            this.setSignupFormField({ key: 'secondaryFixedDesign', value: { name: designWithMostStock.name, slug: designWithMostStock.reference } })
          }
        }
      },
      checkForFeaturedDesigns () {
        const membershipType = this.membershipTypes.find(membershipType => membershipType.orientation === this.orientation)

        if (!membershipType) {
          return
        }

        const productGroupSizes = membershipType.productGroups[0]?.productGroupSizes

        if (!productGroupSizes) {
          return
        }

        let isFeaturedDesign = null

        for (const productGroupSize of productGroupSizes) {
          const featuredProduct = productGroupSize.products.find(product => product.design.isFeatured)

          if (featuredProduct) {
            isFeaturedDesign = featuredProduct.design
            break
          }
        }

        if (isFeaturedDesign) {
          this.setSignupFormField({ key: 'design', value: { name: isFeaturedDesign.name, slug: isFeaturedDesign.reference } })
          this.setSignupFormField({ key: 'freeDesign', value: { name: isFeaturedDesign.name, slug: isFeaturedDesign.reference } })
          this.setSignupFormField({ key: 'fixedDesign', value: { name: isFeaturedDesign.name, slug: isFeaturedDesign.reference } })

          this.designResponse = {
            ...(this.designResponse ? this.designResponse : {}),
            primaryColor: isFeaturedDesign.primaryColor,
            secondaryColor: isFeaturedDesign.secondaryColor
          }
        }
      },
      resetSizes ({ type = 'primary' } = {}) {
        if (type === 'primary') {
          if (this.isWomenOnePager) {
            this.setSignupFormField({
              key: Orientations.womenbralette,
              value: {
                ...this.form[Orientations.womenbralette],
                ...{ size: { label: null, reference: null } }
              }
            })

            return
          }

          this.setSignupFormField({ key: 'size', value: { label: null, reference: null } })
          this.setSignupFormField({ key: 'freeSize', value: { label: null, reference: null } })
        } else if (type === 'secondary') {
          this.setSignupFormField({
            key: Orientations.womenhipster,
            value: {
              ...this.form[Orientations.womenhipster],
              ...{ size: { label: null, reference: null } }
            }
          })
        }
      },
      ...mapActions('lookbook', {
        getSingleDesign: GET_SINGLE_DESIGN
      }),
      ...mapActions('productGroup', {
        getMembershipTypes: GET_MEMBERSHIP_TYPES
      }),
      ...mapActions('flags', {
        getFlags: GET_FLAGS
      }),
      ...mapMutations('signup', {
        setSignupFormField: SET_FORM_FIELD
      }),
      ...mapActions('signup', {
        getLatestDesign: SIGNUP_GET_LATEST_DESIGN
      })
    }
  }
}
